import React from "react";
import ReactDOM from "react-dom";
import {Provider} from "react-redux";
import {withRouter} from "react-router";

import {Auth, UIConfigContext} from "@digatex/digatex-ui-lib";
import {message} from "antd";
import axios from "axios";
import {ConnectedRouter} from "connected-react-router";
import {createBrowserHistory} from "history";

import configureStore from "./configureStore";
import {CurrentUserProvider} from "./contexts/AuthContext";
import {JIRA_DATA_KEY, UI_CONFIG} from "./properties";
import {AHRouter} from "./Router";
import {loadVariants} from "./variants";

import "./index.less";

import "react-app-polyfill/ie9";
import "url-search-params-polyfill";
import {HelmetProvider} from "react-helmet-async";

const history = createBrowserHistory();
const Router = withRouter(AHRouter);
export const store = configureStore();

axios.interceptors.request.use(config => {
	if (Auth.isAuthenticated()) {
		const cb = () => {
			config.headers = {...config.headers, ...Auth.headers()};
			return Promise.resolve(config);
		};
		return Auth.updateToken(cb);
	}
});

function jiraHelpdesk(callback) {
	var jhdScript = document.createElement("script");
	jhdScript.type = "text/javascript";
	jhdScript.setAttribute("data-jsd-embedded", null);
	jhdScript.setAttribute("data-key", JIRA_DATA_KEY);
	jhdScript.setAttribute("data-base-url", "https://jsd-widget.atlassian.com");
	jhdScript.src = "https://jsd-widget.atlassian.com/assets/embed.js";
	if (jhdScript.readyState) {
		jhdScript.onreadystatechange = function () {
			if (jhdScript.readyState === "loaded" || jhdScript.readyState === "complete") {
				jhdScript.onreadystatechange = null;
				callback();
			}
		};
	} else {
		jhdScript.onload = function () {
			callback();
		};
	}
	document.head.appendChild(jhdScript);
}
jiraHelpdesk(function () {
	const DOMContentLoaded_event = new Event("DOMContentLoaded", {bubbles: true, cancelable: true});
	window.document.dispatchEvent(DOMContentLoaded_event);
});

export async function extractErrorMessage(error) {
	const response = error.response;
	if (!response) {
		return "Network error: unable to reach the server";
	}
	const status = response.status;
	let data = response.data;
	if (data instanceof Blob) {
		try {
			const text = await data.text();
			try {
				data = JSON.parse(text);
			} catch {
				return text || `Request failed with status ${status}`;
			}
		} catch {
			return `Request failed with status ${status}`;
		}
	}

	if (data && typeof data === "object") {
		const msg = data.message ?? data.msg ?? data.error ?? data.detail;
		if (msg != null) return String(msg);
		if (data.errors) {
			let messages;
			if (Array.isArray(data.errors)) {
				messages = data.errors;
			} else if (typeof data.errors === "object") {
				messages = Object.values(data.errors).flatMap(v =>
					Array.isArray(v) ? v : [v]
				);
			}
			if (messages) {
				const joined = messages
					.filter(m => m != null && String(m).trim().length > 0)
					.map(m => String(m))
					.join("; ");
				if (joined.length > 0) return joined;
			}
		}
	}
	if (typeof data === "string" && data.length > 0) return data;
	return `Request failed with status ${status}`;
}

axios.interceptors.response.use(
	response => response,
	error => {
		const status = error.response?.status;
		if (status === 403) {
			return Promise.reject(error);
		}
		if ((status && status >= 400 && status <= 599) || !error.response) {
			if (error.config?.omitGlobalError) {
				return Promise.reject(error);
			}
			return extractErrorMessage(error).then(msg => {
				message.error(msg);
				return Promise.reject(error);
			});
		}
		return Promise.reject(error);
	},
);

Auth.init(() =>
	loadVariants().then(() =>
		ReactDOM.render(
            <HelmetProvider>
                <Provider store={store}>
                    <ConnectedRouter history={history}>
                        <UIConfigContext.Provider value={UI_CONFIG}>
                            <CurrentUserProvider>
                                <Router />
                            </CurrentUserProvider>
                        </UIConfigContext.Provider>
                    </ConnectedRouter>
                </Provider>
            </HelmetProvider>,
			document.getElementById("root"),
		),
	),
);
